/**
 * PublishPanel — 站点发布中心，作为左侧栏面板使用.
 *
 * 单文档发布已迁移到 Explorer 右键的独立流程；此处只负责站点级发布。
 */

import {
  AlertCircle,
  Check,
  Copy,
  ExternalLink,
  FolderOpen,
  Loader2,
  Play,
  Square,
} from "lucide-react";
import { useT } from "@magic-md/i18n/react";
import { useCallback, useMemo, useState } from "react";
import { getPublishPresentationStyleVariables } from "../lib/publish-utils";
import { cn } from "../lib/utils";
import { getPlatformService } from "../platform";
import type { SitePublishTaskState } from "../store/publish-store";
import { usePublishStore } from "../store/publish-store";
import {
  getThemeAccentStyle,
  type ThemeAccent,
} from "../theme/runtime/theme-accents";
import { useThemeStore } from "../theme/runtime/theme-store";
import { showFolderPickerDialog } from "./dialogs/DialogHost";
import { SidebarCloseButton } from "./SidebarCloseButton";

function getPageStatusAccent(
  status: "built" | "cached" | "failed",
): ThemeAccent {
  return status === "built"
    ? "success"
    : status === "cached"
      ? "warning"
      : "error";
}

export function PublishPanel() {
  const t = useT("editor");
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between h-8 px-3 border-b border-border shrink-0">
        <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
          {t("publish.title")}
        </span>
        <SidebarCloseButton />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-3">
        <SitePanel />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// Site (multi-document) publish panel
// ═══════════════════════════════════════════════════════════

function SitePanel() {
  const t = useT("editor");
  const siteTask = usePublishStore((s) => s.siteTask);
  const startSitePublish = usePublishStore((s) => s.startSitePublish);
  const cancelSitePublish = usePublishStore((s) => s.cancelSitePublish);
  const clearSiteTask = usePublishStore((s) => s.clearSiteTask);
  const siteServer = usePublishStore((s) => s.siteServer);
  const startSiteServer = usePublishStore((s) => s.startSiteServer);
  const stopSiteServer = usePublishStore((s) => s.stopSiteServer);
  const contentProfile = useThemeStore((s) => s.contentProfile);
  const presentationProfile = useThemeStore((s) => s.presentationProfile);
  const resolvedThemeId = useThemeStore((s) => s.resolvedThemeId);

  const [workspacePath, setWorkspacePath] = useState("");
  const [outDir, setOutDir] = useState("");
  const [siteTitle, setSiteTitle] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [includePattern, setIncludePattern] = useState("");
  const [excludePattern, setExcludePattern] = useState("");
  const [clean, setClean] = useState(false);
  const [rerunAll, setRerunAll] = useState(false);
  const [incremental, setIncremental] = useState(true);
  const [copyFeedback, setCopyFeedback] = useState(false);

  const isRunning = siteTask?.status === "running";
  const isCompleted = siteTask?.status === "completed";
  const isFailed = siteTask?.status === "failed";
  const presentationStyleVariables = useMemo(
    () =>
      getPresentationStyleVariables({
        contentProfileId: contentProfile,
        presentationProfileId: presentationProfile,
        themeId: resolvedThemeId,
      }),
    [contentProfile, presentationProfile, resolvedThemeId],
  );

  const handleCopySiteUrl = useCallback(() => {
    if (siteTask?.result?.outputDir) {
      const indexPath =
        siteTask.result.outputDir.replace(/\/$/, "") + "/index.html";
      navigator.clipboard.writeText(`file://${indexPath}`);
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    }
  }, [siteTask?.result?.outputDir]);

  const handleStart = () => {
    if (!workspacePath.trim()) return;
    clearSiteTask();
    startSitePublish({
      workspacePath: workspacePath.trim(),
      outDir: outDir.trim() || undefined,
      siteTitle: siteTitle.trim() || undefined,
      baseUrl: baseUrl.trim() || undefined,
      presentationProfileId: presentationProfile,
      presentationStyleVariables,
      include: includePattern.trim()
        ? includePattern.split(",").map((s) => s.trim())
        : undefined,
      exclude: excludePattern.trim()
        ? excludePattern.split(",").map((s) => s.trim())
        : undefined,
      clean,
      rerunAll,
      incremental,
    });
  };

  return (
    <div className="space-y-4">
      {/* Configuration — shown when idle */}
      {!isRunning && !isCompleted && (
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-[10px] font-medium text-muted-foreground">
              {t("publish.field.workspacePath")}
            </label>
            <div className="flex gap-1">
              <input
                type="text"
                value={workspacePath}
                onChange={(e) => setWorkspacePath(e.target.value)}
                placeholder="/path/to/workspace"
                className="flex-1 h-7 rounded border border-border bg-background px-2 text-[11px] font-mono"
              />
              <button
                type="button"
                onClick={async () => {
                  const result = await showFolderPickerDialog({
                    title: t("publish.field.workspacePath"),
                  });
                  if (result) setWorkspacePath(result);
                }}
                className="h-7 px-2 rounded border border-border bg-muted hover:bg-muted/80 text-muted-foreground transition-colors shrink-0"
                title={t("settings.directory.pick")}
              >
                <FolderOpen size={12} />
              </button>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-medium text-muted-foreground">
              {t("publish.field.siteTitle")}
            </label>
            <input
              type="text"
              value={siteTitle}
              onChange={(e) => setSiteTitle(e.target.value)}
              placeholder={t("publish.placeholder.siteTitle")}
              className="w-full h-7 rounded border border-border bg-background px-2 text-[11px] font-mono"
            />
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-medium text-muted-foreground">
              {t("publish.field.baseUrl")}
            </label>
            <input
              type="text"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://example.com"
              className="w-full h-7 rounded border border-border bg-background px-2 text-[11px] font-mono"
            />
            <p className="text-[10px] text-muted-foreground/60">
              {t("publish.baseUrlHelp")}
            </p>
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-medium text-muted-foreground">
              {t("publish.field.outputDir")}
            </label>
            <input
              type="text"
              value={outDir}
              onChange={(e) => setOutDir(e.target.value)}
              placeholder=".magic-md/publish/site/"
              className="w-full h-7 rounded border border-border bg-background px-2 text-[11px] font-mono"
            />
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-medium text-muted-foreground">
              {t("publish.field.include")}{" "}
              <span className="text-[9px] text-muted-foreground/50">
                {t("publish.commaSeparated")}
              </span>
            </label>
            <input
              type="text"
              value={includePattern}
              onChange={(e) => setIncludePattern(e.target.value)}
              placeholder="docs/**/*.md"
              className="w-full h-7 rounded border border-border bg-background px-2 text-[11px] font-mono"
            />
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-medium text-muted-foreground">
              {t("publish.field.exclude")}{" "}
              <span className="text-[9px] text-muted-foreground/50">
                {t("publish.commaSeparated")}
              </span>
            </label>
            <input
              type="text"
              value={excludePattern}
              onChange={(e) => setExcludePattern(e.target.value)}
              placeholder="drafts/*"
              className="w-full h-7 rounded border border-border bg-background px-2 text-[11px] font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={incremental}
                onChange={(e) => setIncremental(e.target.checked)}
                className="rounded"
              />
              <span className="text-[11px]">{t("publish.option.incremental")}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={clean}
                onChange={(e) => setClean(e.target.checked)}
                className="rounded"
              />
              <span className="text-[11px]">{t("publish.option.clean")}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={rerunAll}
                onChange={(e) => setRerunAll(e.target.checked)}
                className="rounded"
              />
              <span className="text-[11px]">{t("publish.option.rerunAll")}</span>
            </label>
          </div>

          <button
            type="button"
            onClick={handleStart}
            disabled={!workspacePath.trim()}
            className={cn(
              "w-full py-1.5 rounded-md text-xs font-medium transition-all",
              workspacePath.trim()
                ? "bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80"
                : "bg-muted text-muted-foreground cursor-not-allowed",
            )}
          >
            {t("publish.action.startSite")}
          </button>
        </div>
      )}

      {/* Progress */}
      {isRunning && siteTask?.progress && (
        <SiteProgressView task={siteTask} onCancel={cancelSitePublish} />
      )}

      {/* Completed */}
      {isCompleted && siteTask?.result && (
        <div className="space-y-3">
          <div
            className="flex items-center gap-1.5 theme-accent-text"
            style={getThemeAccentStyle("success")}
          >
            <Check size={14} />
            <span className="text-[11px] font-medium">{t("publish.completed")}</span>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-1.5">
            <StatCard label={t("publish.stat.total")} value={siteTask.result.totalPages} />
            <StatCard
              label={t("publish.stat.built")}
              value={siteTask.result.builtPages}
              color="success"
            />
            <StatCard
              label={t("publish.stat.cached")}
              value={siteTask.result.cachedPages}
              color="warning"
            />
            <StatCard
              label={t("publish.stat.failed")}
              value={siteTask.result.failedPages}
              color="error"
            />
          </div>

          {/* Page list */}
          {siteTask.result.pages.length > 0 && (
            <div className="space-y-1">
              <label className="text-[10px] font-medium text-muted-foreground">
                {t("publish.pageList")}
              </label>
              <div className="max-h-40 overflow-auto rounded border border-border bg-muted/20 divide-y divide-border">
                {siteTask.result.pages.map((page) => (
                  <div
                    key={page.slug}
                    className="flex items-center gap-1.5 px-2 py-1"
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full shrink-0 theme-accent-fill"
                      style={getThemeAccentStyle(
                        getPageStatusAccent(page.status),
                      )}
                    />
                    <span
                      className="text-[10px] font-mono truncate flex-1"
                      title={page.slug}
                    >
                      /{page.slug}
                    </span>
                    <span className="text-[9px] text-muted-foreground shrink-0">
                      {page.status === "built"
                        ? t("publish.status.built")
                        : page.status === "cached"
                          ? t("publish.status.cached")
                          : t("publish.status.failed")}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Warnings */}
          {siteTask.result.warnings.length > 0 && (
            <div
              className="rounded border p-2 space-y-0.5 theme-accent-banner"
              style={getThemeAccentStyle("warning")}
            >
              {siteTask.result.warnings.map((w, i) => (
                <p key={i} className="text-[9px]">
                  {w}
                </p>
              ))}
            </div>
          )}

          {/* Output dir */}
          <div className="space-y-1">
            <label className="text-[10px] font-medium text-muted-foreground">
              {t("publish.field.outputDir")}
            </label>
            <div className="flex items-center gap-1 bg-muted/50 rounded px-2 py-1.5">
              <FolderOpen
                size={11}
                className="text-muted-foreground shrink-0"
              />
              <span
                className="text-[10px] font-mono truncate flex-1"
                title={siteTask.result.outputDir}
              >
                {siteTask.result.outputDir}
              </span>
            </div>
          </div>

          {/* Site server */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-medium text-muted-foreground">
              {t("publish.previewServer")}
            </label>
            {siteServer?.running ? (
              <div
                className="rounded border p-2 space-y-1.5 theme-accent-banner"
                style={getThemeAccentStyle("success")}
              >
                <div className="flex items-center gap-1.5">
                  <span className="relative flex h-2 w-2">
                    <span
                      className="animate-ping absolute inline-flex h-full w-full rounded-full theme-accent-fill opacity-75"
                      style={getThemeAccentStyle("success")}
                    />
                    <span
                      className="relative inline-flex rounded-full h-2 w-2 theme-accent-fill"
                      style={getThemeAccentStyle("success")}
                    />
                  </span>
                  <span className="text-[10px] font-medium">{t("publish.serverRunning")}</span>
                </div>
                <a
                  href={siteServer.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-[10px] font-mono text-primary hover:underline break-all"
                  onClick={(e) => {
                    e.preventDefault();
                    getPlatformService().openExternal?.(siteServer.url);
                  }}
                >
                  <ExternalLink size={10} className="shrink-0" />
                  {siteServer.url}
                </a>
                <button
                  type="button"
                  onClick={() => stopSiteServer()}
                  className="w-full flex items-center justify-center gap-1 py-1 rounded border text-[10px] font-medium theme-accent-button"
                  style={getThemeAccentStyle("error")}
                >
                  <Square size={10} />
                  {t("publish.action.stopServer")}
                </button>
              </div>
            ) : siteServer?.starting ? (
              <div className="flex items-center gap-1.5 px-2 py-2 rounded border border-border bg-muted/30">
                <Loader2
                  size={12}
                  className="animate-spin text-muted-foreground"
                />
                <span className="text-[10px] text-muted-foreground">
                  {t("publish.serverStarting")}
                </span>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  if (siteTask?.result?.outputDir) {
                    startSiteServer(siteTask.result.outputDir);
                  }
                }}
                className="w-full flex items-center justify-center gap-1 py-1.5 rounded-md text-[10px] font-medium bg-muted/50 hover:bg-muted/80 text-foreground border border-border transition-colors"
              >
                <Play size={10} />
                {t("publish.action.startPreviewServer")}
              </button>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={handleCopySiteUrl}
              className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-md text-[10px] font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              {copyFeedback ? <Check size={10} /> : <Copy size={10} />}
              {copyFeedback ? t("common.copied") : t("publish.action.copySiteUrl")}
            </button>
          </div>
          <button
            type="button"
            onClick={() => {
              if (siteServer?.running) stopSiteServer();
              clearSiteTask();
            }}
            className="w-full py-1.5 rounded-md text-[10px] font-medium bg-muted/50 hover:bg-muted/80 text-muted-foreground transition-colors"
          >
            {t("publish.action.republish")}
          </button>
        </div>
      )}

      {/* Failed */}
      {isFailed && (
        <div className="space-y-2">
          <div
            className="flex items-center gap-1.5 theme-accent-text"
            style={getThemeAccentStyle("error")}
          >
            <AlertCircle size={14} />
            <span className="text-[11px] font-medium">{t("publish.failed")}</span>
          </div>
          <div
            className="rounded border p-2 theme-accent-banner"
            style={getThemeAccentStyle("error")}
          >
            <p className="text-[10px] font-mono break-all">
              {siteTask?.error ?? t("publish.unknownError")}
            </p>
          </div>
          <button
            type="button"
            onClick={() => clearSiteTask()}
            className="w-full py-1.5 rounded-md text-xs font-medium bg-muted hover:bg-muted/80 transition-colors"
          >
            {t("publish.action.retry")}
          </button>
        </div>
      )}
    </div>
  );
}

function getPresentationStyleVariables(params: {
  themeId: string;
  contentProfileId: string;
  presentationProfileId: string;
}): Record<string, string> {
  return getPublishPresentationStyleVariables(params);
}

// ── Site Progress View ──

function SiteProgressView({
  task,
  onCancel,
}: {
  task: SitePublishTaskState;
  onCancel: () => void;
}) {
  const t = useT("editor");
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-muted-foreground flex items-center gap-1.5">
          <Loader2 size={12} className="animate-spin" />
          {t("publish.running")}
        </span>
        <span className="text-[10px] font-mono text-muted-foreground">
          {task.progress?.percent ?? 0}%
        </span>
      </div>

      <div className="w-full h-1 rounded-full bg-border overflow-hidden">
        <div
          className="h-full rounded-full bg-primary transition-all duration-300 ease-out"
          style={{ width: `${task.progress?.percent ?? 0}%` }}
        />
      </div>

      <p className="text-[10px] text-muted-foreground truncate">
        {task.progress?.message}
      </p>

      {/* Page-level progress */}
      {task.pagesTotal != null && task.pagesTotal > 0 && (
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-[9px] text-muted-foreground">{t("publish.pageProgress")}</span>
            <span className="text-[9px] font-mono text-muted-foreground">
              {task.pagesProcessed ?? 0}/{task.pagesTotal}
            </span>
          </div>
          {task.currentPage && (
            <p
              className="text-[9px] text-muted-foreground/70 font-mono truncate"
              title={task.currentPage}
            >
              → {task.currentPage}
            </p>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={onCancel}
        className="w-full py-1.5 rounded-md border text-xs font-medium theme-accent-button"
        style={getThemeAccentStyle("error")}
      >
        {t("publish.action.cancel")}
      </button>
    </div>
  );
}

// ── Helpers ──

function StatCard({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color?: ThemeAccent;
}) {
  return (
    <div className="rounded border border-border bg-muted/30 p-1.5 text-center">
      <div
        className={cn(
          "text-sm font-bold font-mono",
          color ? "theme-accent-text" : "text-foreground",
        )}
        style={color ? getThemeAccentStyle(color) : undefined}
      >
        {value}
      </div>
      <div className="text-[9px] text-muted-foreground">{label}</div>
    </div>
  );
}
